import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AdminPageHeader, AdminModal, AdminField } from "./AdminBits";
import { Eye, Save } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "new", label: "Yeni Başvuru", cls: "bg-blue-50 text-blue-700" },
  { value: "reviewing", label: "İnceleniyor", cls: "bg-yellow-50 text-yellow-700" },
  { value: "need_info", label: "Ek Bilgi Bekleniyor", cls: "bg-orange-50 text-orange-700" },
  { value: "accepted", label: "Kabul Edildi", cls: "bg-green-50 text-green-700" },
  { value: "rejected", label: "Reddedildi", cls: "bg-red-50 text-red-700" },
  { value: "archived", label: "Arşivlendi", cls: "bg-gray-50 text-gray-600" },
];

function StatusPill({ status }) {
  const s = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
  return <span className={`text-xs px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
}

function ApplicationsBase({ endpoint, columnsExtra, title, testidPrefix, exportFields }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("new");

  const load = async () => {
    const q = filter ? `?status=${filter}` : "";
    const { data } = await api.get(`${endpoint}${q}`);
    setItems(data);
  };
  useEffect(() => { load(); }, [filter]);

  const openDetail = (r) => { setSelected(r); setNote(r.admin_note || ""); setStatus(r.status || "new"); };
  const saveUpdate = async () => {
    try {
      await api.patch(`${endpoint}/${selected.id}`, { status, admin_note: note });
      toast.success("Güncellendi");
      setSelected(null); load();
    } catch (e) { toast.error("Hata"); }
  };

  const exportCsv = () => {
    if (items.length === 0) return;
    const keys = Object.keys(items[0]).filter(k => !["_id"].includes(k));
    const csv = [keys.join(","), ...items.map(r => keys.map(k => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${testidPrefix}-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid={`admin-${testidPrefix}`}>
      <AdminPageHeader title={title} subtitle={`${items.length} başvuru`} action={
        <div className="flex items-center gap-3">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-[#E5E7EB] text-sm bg-white" data-testid={`${testidPrefix}-filter`}>
            <option value="">Tüm Durumlar</option>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button onClick={exportCsv} className="btn-outline !py-2 !px-4 text-sm" data-testid={`${testidPrefix}-export`}>CSV</button>
        </div>
      }/>

      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F9FA] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-start px-4 py-3 font-medium text-[#4B5563] text-xs uppercase tracking-wider">Ad</th>
                <th className="text-start px-4 py-3 font-medium text-[#4B5563] text-xs uppercase tracking-wider">E-posta</th>
                {columnsExtra.map(c => <th key={c.key} className="text-start px-4 py-3 font-medium text-[#4B5563] text-xs uppercase tracking-wider">{c.label}</th>)}
                <th className="text-start px-4 py-3 font-medium text-[#4B5563] text-xs uppercase tracking-wider">Durum</th>
                <th className="text-start px-4 py-3 font-medium text-[#4B5563] text-xs uppercase tracking-wider">Tarih</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={5 + columnsExtra.length} className="py-10 text-center text-[#4B5563]">Başvuru yok</td></tr>
              ) : items.map(r => (
                <tr key={r.id} className="border-b border-[#E5E7EB] last:border-0 hover:bg-[#F7F9FA]/50">
                  <td className="px-4 py-3 font-medium text-[#102A43]">{r.name || r.contact_name}</td>
                  <td className="px-4 py-3 text-[#4B5563]">{r.email}</td>
                  {columnsExtra.map(c => <td key={c.key} className="px-4 py-3 text-[#4B5563]">{c.render ? c.render(r) : r[c.key]}</td>)}
                  <td className="px-4 py-3"><StatusPill status={r.status}/></td>
                  <td className="px-4 py-3 text-xs text-[#4B5563]">{new Date(r.created_at).toLocaleDateString("tr-TR")}</td>
                  <td className="px-4 py-3 text-end">
                    <button onClick={() => openDetail(r)} className="p-1.5 text-[#1F8A5B] hover:bg-[#EAF7F0] rounded" data-testid={`${testidPrefix}-view-${r.id}`}><Eye className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AdminModal open={!!selected} onClose={() => setSelected(null)} title="Başvuru Detayı" testid={`${testidPrefix}-detail`}>
        {selected && (
          <div className="space-y-3 text-sm">
            {Object.entries(selected).filter(([k]) => !["_id", "id", "admin_note", "status", "kvkk_approved", "consent_approved"].includes(k)).map(([k, v]) => (
              <div key={k} className="grid grid-cols-3 gap-3 py-2 border-b border-[#E5E7EB] last:border-0">
                <div className="text-xs text-[#4B5563] uppercase tracking-wider">{k}</div>
                <div className="col-span-2 text-[#102A43] break-words">{Array.isArray(v) ? v.join(", ") : String(v || "-")}</div>
              </div>
            ))}
            <div className="pt-3 border-t border-[#E5E7EB]">
              <label className="block text-xs font-medium text-[#4B5563] mb-1 uppercase tracking-wider">Durum</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] text-sm" data-testid={`${testidPrefix}-status-select`}>
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1 uppercase tracking-wider">Admin Notu</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] text-sm" data-testid={`${testidPrefix}-note`}/>
            </div>
            <div className="flex justify-end gap-3 pt-3">
              <button onClick={() => setSelected(null)} className="btn-outline !py-2 !px-4 text-sm">İptal</button>
              <button onClick={saveUpdate} className="btn-primary !py-2 !px-4 text-sm" data-testid={`${testidPrefix}-save`}><Save className="w-4 h-4"/> Kaydet</button>
            </div>
          </div>
        )}
      </AdminModal>
    </div>
  );
}

export function AdminMembershipApplications() {
  return <ApplicationsBase endpoint="/applications/membership" testidPrefix="membership" title="Üyelik Başvuruları" columnsExtra={[
    { key: "membership_type", label: "Tip" },
    { key: "company_name", label: "Firma" },
    { key: "phone", label: "Telefon" },
  ]}/>;
}
export function AdminVolunteerApplications() {
  return <ApplicationsBase endpoint="/applications/volunteer" testidPrefix="volunteer" title="Gönüllü Başvuruları" columnsExtra={[
    { key: "city", label: "Şehir" },
    { key: "profession", label: "Meslek" },
    { key: "volunteer_fields", label: "Alanlar", render: r => (r.volunteer_fields || []).join(", ") },
  ]}/>;
}
export function AdminPartnershipApplications() {
  return <ApplicationsBase endpoint="/applications/partnership" testidPrefix="partnership" title="Kurumsal İş Birliği Başvuruları" columnsExtra={[
    { key: "company_name", label: "Firma" },
    { key: "partnership_type", label: "Tür" },
    { key: "country", label: "Ülke" },
  ]}/>;
}
export function AdminContactMessages() {
  return <ApplicationsBase endpoint="/contact" testidPrefix="contact" title="İletişim Mesajları" columnsExtra={[
    { key: "subject", label: "Konu" },
    { key: "phone", label: "Telefon" },
  ]}/>;
}
